import { assessmentAPI } from '../services/api';

const PENDING_CAMPAIGN_ID_KEY = 'engage_pending_campaign_id';

export function readCampaignIdFromSearch(search: string): string {
  return new URLSearchParams(search).get('campaignId')?.trim() || '';
}

/** Keep enrol/share campaign id across redirects that drop query params (e.g. /complete-profile). */
export function rememberCampaignId(campaignId: string): void {
  const trimmed = campaignId.trim();
  if (trimmed) {
    window.sessionStorage.setItem(PENDING_CAMPAIGN_ID_KEY, trimmed);
  }
}

export function rememberCampaignIdFromSearch(search: string): void {
  const campaignId = readCampaignIdFromSearch(search);
  if (campaignId) {
    rememberCampaignId(campaignId);
  }
}

export function readPendingCampaignId(): string {
  return window.sessionStorage.getItem(PENDING_CAMPAIGN_ID_KEY)?.trim() || '';
}

export function clearPendingCampaignId(): void {
  window.sessionStorage.removeItem(PENDING_CAMPAIGN_ID_KEY);
}

/** URL query first, then session storage from enrol/signup/sign-in flow. */
export function resolveCampaignId(search: string): string {
  return readCampaignIdFromSearch(search) || readPendingCampaignId();
}

export function completeProfilePath(campaignId?: string): string {
  if (!campaignId) {
    return '/complete-profile';
  }
  return `/complete-profile?campaignId=${encodeURIComponent(campaignId)}`;
}

export function employeePathWithCampaign(campaignId: string): string {
  return `/employee?campaignId=${encodeURIComponent(campaignId)}`;
}

export type EnrolCampaignResult = {
  departmentOptions: string[];
};

function normalizeDepartmentOptions(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return Array.from(new Set(raw.map((entry) => String(entry).trim()).filter(Boolean)));
}

/** Resolve org department list from campaign id (no user/org mutation). */
export async function fetchCampaignEnrolDepartments(campaignId: string): Promise<string[]> {
  const response = await assessmentAPI.getCampaignEnrolContext(campaignId);
  return normalizeDepartmentOptions(response.data?.departmentOptions);
}

/** Link self-serve user to campaign org (share/enrol link) before profile or assessment. */
export async function enrolCampaignIfNeeded(
  campaignId: string,
  refreshUser?: () => Promise<unknown>,
): Promise<EnrolCampaignResult | null> {
  if (!campaignId) {
    return null;
  }
  rememberCampaignId(campaignId);

  let departmentOptions: string[] = [];
  try {
    departmentOptions = await fetchCampaignEnrolDepartments(campaignId);
  } catch {
    // Context fetch failed; assign response may still include departments.
  }

  try {
    const response = await assessmentAPI.assignCampaign(campaignId);
    const fromAssign = normalizeDepartmentOptions(response.data?.departmentOptions);
    departmentOptions = Array.from(new Set([...departmentOptions, ...fromAssign].map((v) => v.trim()).filter(Boolean)));
    if (refreshUser) {
      await refreshUser();
    }
  } catch (assignError) {
    if (departmentOptions.length > 0) {
      return { departmentOptions };
    }
    throw assignError;
  }

  return { departmentOptions };
}

/** After auth, assign campaign (if any) and return destination path. */
export async function resolvePostAuthDestination(
  campaignId: string,
  fallbackPath: string,
  fromPath?: string | null,
  refreshUser?: () => Promise<unknown>,
): Promise<string> {
  if (campaignId) {
    try {
      await enrolCampaignIfNeeded(campaignId, refreshUser);
    } catch {
      // Profile and assessment flows retry assignment.
    }
    return employeePathWithCampaign(campaignId);
  }
  if (fromPath && fromPath.startsWith('/')) {
    return fromPath;
  }
  return fallbackPath;
}
